import React, { createContext, useContext, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, Sparkles, Home, Ticket, MessageSquare, Users, Mail, Wallet, Star, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { TOURS, MEMBER_WELCOME_KEY } from "@/help/content";

// Route -> clean Lucide line icon for the tour/guide cards (replaces the old emojis).
const STEP_ICON = {
  "/": Home, "/events": Ticket, "/community": MessageSquare, "/connections": Users,
  "/messages": Mail, "/wallet": Wallet, "/me": Star, "/jobs": Briefcase,
  "/manage-postings": Briefcase,
};

const TourCtx = createContext(null);
export const useTour = () => useContext(TourCtx);

const PAD = 8; // spotlight padding around the target

export function TourProvider({ children }) {
  const nav = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [tourKey, setTourKey] = useState(null);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const launchedRef = useRef(new Set());

  const tour = tourKey ? TOURS[tourKey] : null;
  const steps = tour?.steps || [];
  const step = steps[idx];

  const start = useCallback((key) => {
    if (!TOURS[key]) return;
    setTourKey(key);
    setIdx(0);
    setRect(null);
  }, []);

  const markSeen = useCallback((key) => {
    setUser((u) => (u ? { ...u, tours_seen: Array.from(new Set([...(u.tours_seen || []), key])) } : u));
    api(`/me/tours/${key}/seen`, { method: "POST" }).catch(() => {});
  }, [setUser]);

  const finish = useCallback(() => {
    if (tourKey) markSeen(tourKey);
    setTourKey(null);
    setRect(null);
  }, [tourKey, markSeen]);

  const next = useCallback(() => {
    if (idx >= steps.length - 1) finish();
    else setIdx((i) => i + 1);
  }, [idx, steps.length, finish]);

  const back = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  // Navigate (member tours) when a step requests a route.
  useEffect(() => {
    if (!step) return;
    if (step.route && location.pathname !== step.route) {
      nav(step.route);
    }
  }, [tourKey, idx]);

  // Measure the spotlight target after layout settles.
  const measure = useCallback(() => {
    if (!step) return;
    let el = null;
    for (const tid of step.targets || []) {
      const e = document.querySelector(`[data-testid="${tid}"]`);
      if (e && e.getClientRects().length) { el = e; break; }
    }
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useLayoutEffect(() => {
    if (!tour) return;
    const el = (step?.targets || []).map((t) => document.querySelector(`[data-testid="${t}"]`)).find(Boolean);
    if (el) el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    const timers = [80, 350, 700].map((ms) => setTimeout(measure, ms));
    return () => timers.forEach(clearTimeout);
  }, [tourKey, idx, tour, step, measure]);

  useEffect(() => {
    if (!tour) return;
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [tour, measure]);

  // Auto-launch the member welcome tour on first login.
  useEffect(() => {
    if (!user || tourKey) return;
    if (user.account_type === "business") return;
    if (user.account_status && user.account_status !== "approved" && !user.is_admin) return;
    if ((user.tours_seen || []).includes(MEMBER_WELCOME_KEY)) return;
    const p = location.pathname;
    const blocked = ["/login", "/register", "/onboarding", "/forgot-password", "/reset-password", "/business/console", "/admin", "/legal"];
    if (blocked.some((b) => p.startsWith(b))) return;
    if (launchedRef.current.has(MEMBER_WELCOME_KEY)) return;
    launchedRef.current.add(MEMBER_WELCOME_KEY);
    const t = setTimeout(() => start(MEMBER_WELCOME_KEY), 900);
    return () => clearTimeout(t);
  }, [user, location.pathname, tourKey, start]);

  return (
    <TourCtx.Provider value={{ start, active: !!tour }}>
      {children}
      {tour && step && (
        <TourOverlay
          step={step}
          rect={rect}
          index={idx}
          total={steps.length}
          onNext={next}
          onBack={back}
          onSkip={finish}
        />
      )}
    </TourCtx.Provider>
  );
}

function TourOverlay({ step, rect, index, total, onNext, onBack, onSkip }) {
  const isLast = index === total - 1;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;

  // Tooltip position: near the target if we have one, else centered.
  let cardStyle = { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
  if (rect) {
    const cardW = Math.min(360, vw - 24);
    const below = rect.top + rect.height + 14;
    const placeBelow = below + 190 < vh;
    let left = rect.left + rect.width / 2 - cardW / 2;
    left = Math.max(12, Math.min(left, vw - cardW - 12));
    const top = placeBelow ? below : Math.max(12, rect.top - 14 - 200);
    cardStyle = { left, top, width: cardW };
  }

  return createPortal(
    <div className="fixed inset-0 z-[200]" data-testid="tour-overlay" style={{ pointerEvents: "auto" }}>
      {/* Backdrop + spotlight */}
      {rect ? (
        <div
          className="absolute rounded-xl transition-all duration-300"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(24,20,39,0.66)",
            border: "2px solid #EF5D41",
          }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: "rgba(24,20,39,0.72)" }} />
      )}

      {/* Coach card */}
      <div
        data-testid="tour-card"
        className="absolute bg-white rounded-2xl shadow-2xl p-5 border border-black/5 animate-rise"
        style={{ maxWidth: 360, ...cardStyle }}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFF1EA] flex items-center justify-center shrink-0">
            {(() => { const Ic = STEP_ICON[step.route] || Sparkles; return <Ic className="w-5 h-5 text-[#EF5D41]" />; })()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-lg text-[#181427] leading-snug" data-testid="tour-title">{step.title}</h3>
          </div>
          <button data-testid="tour-skip" onClick={onSkip} className="p-1 -mr-1 -mt-1 rounded-md hover:bg-black/5 text-[#7A7368]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[14px] text-[#4b4655] mt-2 leading-relaxed" data-testid="tour-body">{step.body}</p>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1.5" data-testid="tour-progress">
            {Array.from({ length: total }).map((_, i) => (
              <span key={i} className="rounded-full transition-all" style={{ width: i === index ? 18 : 6, height: 6, background: i === index ? "#EF5D41" : "#E0DAD0" }} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button data-testid="tour-back" onClick={onBack} className="inline-flex items-center gap-1 text-[13px] text-[#7A7368] px-2 py-1.5 rounded-lg hover:bg-black/5">
                <ArrowLeft className="w-4 h-4" />Back
              </button>
            )}
            <button data-testid="tour-next" onClick={onNext} className="inline-flex items-center gap-1 bg-[#EF5D41] hover:bg-[#e0502f] text-white text-[13px] font-semibold px-4 py-2 rounded-full transition-colors">
              {isLast ? "Done" : "Next"}{!isLast && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {index === 0 && !isLast && (
          <button data-testid="tour-skip-text" onClick={onSkip} className="w-full text-center text-[12px] text-[#9a95a5] mt-2 hover:text-[#7A7368]">
            Skip tour
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
